import { action, makeObservable, observable } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { logger } from "@/modules/logger";
import posthog from "posthog-js";

const NEW_USER_ONBOARDING_VIEWED_STORAGE_KEY_PREFIX =
  "mem:new-user-onboarding-viewed:posthog-session:";

/**
 * Store responsible for tracking analytics events state.
 *
 * This store keeps track of which analytics events have been fired during
 * the session to prevent duplicate events, especially across component
 * mount/unmount cycles.
 */
export class AppStoreMetricsStore extends AppSubStore {
  /**
   * Tracks whether the app_store_initialize event has been fired.
   * This ensures the event only fires once per session.
   */
  hasTrackedAppStoreInitialize = false;

  /**
   * Tracks whether the ui_ready event has been fired.
   * This ensures the event only fires once per session, even if components remount.
   */
  hasTrackedUiReady = false;

  /**
   * Tracks whether the welcome_overlay_view event has been fired.
   * This ensures the event only fires once per browser session.
   */
  hasTrackedWelcomeOverlayView = false;

  private hasTrackedNewUserOnboardingViewedWithoutSessionId = false;
  private trackedNewUserOnboardingViewedSessionIds = new Set<string>();

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      this,
      | "getPosthogSessionId"
      | "hasTrackedNewUserOnboardingViewedWithoutSessionId"
      | "shouldTrackNewUserOnboardingViewed"
      | "trackedNewUserOnboardingViewedSessionIds"
    >(this, {
      hasTrackedAppStoreInitialize: observable,
      hasTrackedUiReady: observable,
      hasTrackedWelcomeOverlayView: observable,
      hasTrackedNewUserOnboardingViewedWithoutSessionId: false,
      trackedNewUserOnboardingViewedSessionIds: false,
      trackAppStoreInitialize: action,
      trackUiReady: action,
      trackWelcomeOverlayView: action,
      trackNewUserOnboardingViewed: action,
      getPosthogSessionId: false,
      shouldTrackNewUserOnboardingViewed: false,
    });
  }

  /**
   * Tracks the app_store_initialize event exactly once per session.
   *
   * This should be called when the AppStore.initialize() method completes successfully:
   * - Account info has been fetched
   * - Spaces and SpaceAccounts have been loaded
   * - Platform info has been initialized
   * - User has been identified in metrics
   * - Observability has been set up
   * - Local and MemDB databases have been initialized
   */
  trackAppStoreInitialize = () => {
    if (this.hasTrackedAppStoreInitialize) {
      return;
    }

    logger.info({
      message: "[AppStoreMetricsStore]: Tracking app_store_initialize event",
    });

    trackEvent(TrackedEvent.AppStoreInitialize);
    this.hasTrackedAppStoreInitialize = true;
  };

  /**
   * Tracks the ui_ready event exactly once per session.
   *
   * This should be called when the app becomes fully interactive:
   * - User authentication is complete
   * - App store initialization is done (user data, observability, databases)
   * - Bootstrap process is complete (all user data downloaded and processed)
   * - Sync system is activated (action queue, polling, Pusher)
   * - UI is ready for user interaction
   */
  trackUiReady = ({ isReady }: { isReady?: boolean }) => {
    if (!isReady || this.hasTrackedUiReady) {
      return;
    }

    logger.info({
      message: "[AppStoreMetricsStore]: Tracking ui_ready event",
    });

    trackEvent(TrackedEvent.UiReady);
    this.hasTrackedUiReady = true;
  };

  /**
   * Tracks the welcome_overlay_view event exactly once per browser session.
   *
   * This should be called when the welcome overlay is shown for the first time
   * in the session.
   */
  trackWelcomeOverlayView = (properties?: Record<string, unknown>) => {
    if (this.hasTrackedWelcomeOverlayView) {
      return;
    }

    logger.info({
      message: "[AppStoreMetricsStore]: Tracking welcome_overlay_view event",
    });

    trackEvent(TrackedEvent.WelcomeOverlayView, properties);
    this.hasTrackedWelcomeOverlayView = true;
  };

  /**
   * Tracks a clean PostHog session-filtering marker for new-user onboarding.
   *
   * This event is intentionally narrower than welcome_overlay_view: it only
   * represents the rendered "How did you hear about Mem?" screen.
   */
  trackNewUserOnboardingViewed = () => {
    const posthogSessionId = this.getPosthogSessionId();
    if (!this.shouldTrackNewUserOnboardingViewed(posthogSessionId)) {
      return;
    }

    logger.info({
      message: "[AppStoreMetricsStore]: Tracking new_user_onboarding_viewed event",
    });

    trackEvent(TrackedEvent.NewUserOnboardingViewed, {
      step_id: "icp-rfi",
      substep_id: "referrer",
      onboarding_version: "web_onboarding_v1",
    });
  };

  private getPosthogSessionId(): string | null {
    try {
      return posthog.get_session_id() ?? null;
    } catch {
      return null;
    }
  }

  private shouldTrackNewUserOnboardingViewed(posthogSessionId: string | null): boolean {
    if (!posthogSessionId) {
      if (this.hasTrackedNewUserOnboardingViewedWithoutSessionId) {
        return false;
      }

      this.hasTrackedNewUserOnboardingViewedWithoutSessionId = true;
      return true;
    }

    if (this.trackedNewUserOnboardingViewedSessionIds.has(posthogSessionId)) {
      return false;
    }

    const storageKey = `${NEW_USER_ONBOARDING_VIEWED_STORAGE_KEY_PREFIX}${posthogSessionId}`;
    try {
      if (sessionStorage.getItem(storageKey) === "true") {
        this.trackedNewUserOnboardingViewedSessionIds.add(posthogSessionId);
        return false;
      }

      sessionStorage.setItem(storageKey, "true");
    } catch {
      // Fall back to in-memory dedupe when sessionStorage is unavailable.
    }

    this.trackedNewUserOnboardingViewedSessionIds.add(posthogSessionId);
    return true;
  }
}
